import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';
// import Card from '../components/ui/Card';
// Removed vector logo component; using raster image from public folder
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
// Banner assets
import ban1 from '../assets/ban-1.jpeg';
import ban2 from '../assets/ban-2.jpeg';
import logoFinal from '../assets/logo-final.jpeg';
 

const { FiChevronLeft, FiChevronRight } = FiIcons;

const LandingPage = () => {
  const { isAuthenticated, user } = useAuth();
  // Dynamic services state with static fallbacks
  const [studentServices, setStudentServices] = useState([]);
  const [jobServices, setJobServices] = useState([]);
  const [otherServices, setOtherServices] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Static fallback lists preserve existing design
  const staticStudent = [
    { id: 'jee-mains', name: 'JEE Mains', subtitle: 'Joint Entrance Examination', icon: FiIcons.FiBook, bg: 'from-blue-500 to-blue-600' },
    { id: 'neet', name: 'NEET', subtitle: 'Medical Entrance Exam', icon: FiIcons.FiAward, bg: 'from-green-500 to-green-600' },
    { id: 'vit', name: 'VIT', subtitle: 'Vellore Institute of Technology', icon: FiIcons.FiGraduationCap, bg: 'from-purple-500 to-purple-600' },
    { id: 'amrita', name: 'AMRITA', subtitle: 'Vishwa Vidyapeetham', icon: FiIcons.FiBook, bg: 'from-indigo-500 to-indigo-600' },
    { id: 'srm', name: 'SRM', subtitle: 'Institute of Science & Technology', icon: FiIcons.FiGraduationCap, bg: 'from-red-500 to-red-600' },
    { id: 'bitsat', name: 'BITSAT', subtitle: 'BITS Admission Test', icon: FiIcons.FiBook, bg: 'from-yellow-500 to-yellow-600' },
    { id: 'iiit-hyderabad', name: 'IIIT Hyderabad', subtitle: 'International Institute of IT', icon: FiIcons.FiGraduationCap, bg: 'from-teal-500 to-teal-600' },
    { id: 'tg-eapcet', name: 'TG EAPCET', subtitle: 'Telangana EAPCET', icon: FiIcons.FiBook, bg: 'from-orange-500 to-orange-600' },
    { id: 'ap-eapcet', name: 'AP EAPCET', subtitle: 'Andhra Pradesh EAPCET', icon: FiIcons.FiGraduationCap, bg: 'from-pink-500 to-pink-600' },
    { id: 'other-universities', name: 'Other Universities', subtitle: 'View all institutions', icon: FiIcons.FiLayers, bg: 'from-gray-600 to-gray-800' },
  ];
  const staticJobs = [
    { id: 'resume-building', name: 'Resume Building', icon: FiIcons.FiEdit, to: '/service-details/resume-building', bg: 'from-orange-500 to-amber-600' },
    { id: 'career-guidance', name: 'Career Guidance', icon: FiIcons.FiCompass, to: '/service-details/career-guidance', bg: 'from-blue-500 to-indigo-600' },
    { id: 'government-job-forms', name: 'Government Jobs', icon: FiIcons.FiBriefcase, to: '/service-details/government-job-forms', bg: 'from-gray-700 to-slate-800' },
    { id: 'job-applications', name: 'Job Applications', icon: FiIcons.FiFileText, to: '/service-details/job-applications', bg: 'from-purple-500 to-violet-600' },
  ];
  const staticOther = [
    { id: 'passport-applications', name: 'Passport', icon: FiIcons.FiGlobe, to: '/service-details/passport-applications', bg: 'from-indigo-500 to-blue-600' },
    { id: 'visa-applications', name: 'Visa Services', icon: FiIcons.FiPlane, to: '/service-details/visa-applications', bg: 'from-emerald-500 to-teal-600' },
    { id: 'document-verification', name: 'Document Verification', icon: FiIcons.FiCheckCircle, to: '/service-details/document-verification', bg: 'from-teal-500 to-cyan-600' },
    { id: 'certificate-services', name: 'Certificates', icon: FiIcons.FiAward, to: '/service-details/certificate-services', bg: 'from-fuchsia-500 to-pink-600' },
  ];

  // Helpers
  const generateId = (name = '') => name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
  const isRowEnabled = (row) => {
    const dp = row?.detailed_pricing || {};
    if (typeof dp.enabled === 'boolean') return dp.enabled;
    if (typeof row?.enabled === 'boolean') return row.enabled;
    if (typeof row?.is_enabled === 'boolean') return row.is_enabled;
    if (typeof row?.active === 'boolean') return row.active;
    return true;
  };
  const colorPalette = [
    'from-blue-500 to-blue-600','from-green-500 to-green-600','from-purple-500 to-purple-600','from-red-500 to-red-600','from-indigo-500 to-indigo-600','from-yellow-500 to-yellow-600','from-pink-500 to-pink-600','from-teal-500 to-teal-600','from-orange-500 to-orange-600','from-cyan-500 to-cyan-600','from-lime-500 to-lime-600','from-amber-500 to-amber-600','from-emerald-500 to-emerald-600','from-violet-500 to-violet-600','from-fuchsia-500 to-fuchsia-600','from-rose-500 to-rose-600'
  ];

  // Fetch and subscribe
  useEffect(() => {
    let colorIndex = 0;
    const pickColor = () => colorPalette[colorIndex++ % colorPalette.length];

    const fetchServices = async () => {
      try {
        const { data, error } = await supabase.from('services_dy3xr').select('*');
        if (error) throw error;
        if (!data || data.length === 0) {
          setStudentServices(staticStudent);
          setJobServices(staticJobs);
          setOtherServices(staticOther);
          setLoaded(true);
          return;
        }

        // unique by name
        const unique = data.filter((svc, i, arr) => i === arr.findIndex(s => s.name === svc.name));
        const enabled = unique.filter(isRowEnabled);

        const toCard = (row) => {
          const id = generateId(row.name);
          const icon = FiIcons[row.icon] || FiIcons.FiFileText;
          const bg = row.color || pickColor();
          return { id, name: row.name, subtitle: row.subtitle, icon, bg, to: `/service-details/${id}` };
        };

        const mapped = enabled.map(row => {
          let categoryId = row.category || 'other';
          if (categoryId === 'student' || categoryId === 'education') categoryId = 'application';
          if (categoryId === 'job' || categoryId === 'job-seeker' || /job|resume|career/i.test(row.name || '')) categoryId = 'job-services';
          return { categoryId, card: toCard(row) };
        });

        const appCards = mapped.filter(m => m.categoryId === 'application').map(m => m.card);
        const jobCards = mapped.filter(m => m.categoryId === 'job-services').map(m => m.card);
        const otherCards = mapped.filter(m => m.categoryId !== 'application' && m.categoryId !== 'job-services').map(m => m.card);

        // If any section empty, fall back to static to preserve look density
        setStudentServices(appCards.length ? appCards : staticStudent);
        setJobServices(jobCards.length ? jobCards : staticJobs);
        setOtherServices(otherCards.length ? otherCards : staticOther);
        setLoaded(true);
      } catch (e) {
        // use fallbacks on error
        setStudentServices(staticStudent);
        setJobServices(staticJobs);
        setOtherServices(staticOther);
        setLoaded(true);
      }
    };

    fetchServices();

    const channel = supabase
      .channel('lp-services-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'services_dy3xr' }, () => fetchServices())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Slider helper for Job Services (below banner)
  const jobSliderRef = useRef(null);
  const scrollJobSlider = (dir = 1) => {
    const el = jobSliderRef.current;
    if (!el) return;
    const firstCard = el.querySelector('[data-job-card]');
    const step = firstCard ? firstCard.clientWidth + 12 : Math.max(240, el.clientWidth * 0.7);
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  // Banner slider (mobile) images and auto-scroll
  const bannerImages = [ban1, ban2];
  const mobileBannerRef = useRef(null);
  useEffect(() => {
    const el = mobileBannerRef.current;
    if (!el) return;
    let rafId;
    const speed = 1.2; // pixels per frame ~72px/s at 60fps
    const step = () => {
      const half = el.scrollWidth / 2;
      if (half > 0) {
        el.scrollLeft = (el.scrollLeft + speed) % half;
      }
      rafId = requestAnimationFrame(step);
    };
    // start from 0
    el.scrollLeft = 0;
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <div className="min-h-screen">
      <section className="py-2 sm:py-16 bg-gradient-to-br from-sky-600 to-cyan-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-lg sm:text-4xl lg:text-5xl font-extrabold leading-tight">
                <span className="text-amber-300 text-md">Application Process</span>
                <span className="text-md text-white mt-1">- "SIMPLIFIED"</span>
              </h1>
              <p className="mt-2 md:mt-5 text-white/90 text-sm sm:text-base max-w-xl">Professional online application services.</p>
              <p className="text-white/90 text-sm sm:text-base max-w-xl">Experts in all online applications.</p>

              <ul className="mt-2 md:mt-6 space-y-3 text-white/95 text-sm sm:text-base">
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="mt-0.5 w-5 h-5 text-emerald-300" />
                  <span>Just Rs. 100/-* Service Per Application</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="mt-0.5 w-5 h-5 text-emerald-300" />
                  <span>Order From Home - Delivered on Phone</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="mt-0.5 w-5 h-5 text-emerald-300" />
                  <span>Avoid Queues - Save Time</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="mt-0.5 w-5 h-5 text-emerald-300" />
                  <span>Expert Service - Peace of Mind</span>
                </li>
              </ul>

              <p className="mt-3 text-white/70 text-xs">*Bank Charges and Taxess, Taxes Extra</p>

              <div className="mt-2 md:mt-6">
                <Link to={isAuthenticated && user?.role === 'customer' ? '/dashboard' : '/register'}>
                  <Button size="lg" className="bg-amber-400 hover:bg-amber-500 text-gray-900 font-semibold shadow">
                    APPLY NOW
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="flex justify-center"
            >
              <div className="bg-white rounded-2xl shadow-xl p-6 hidden md:block md:p-8 w-full max-w-sm">
                <div className="aspect-square flex items-center justify-center">
                  <img src={logoFinal} alt="How2Apply Logo" className="w-full h-auto" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Student Services Section */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl lg:text-4xl font-bold text-gray-900">Student Services</h2>
            <p className="mt-3 text-gray-600 text-lg">Comprehensive support for all your academic application needs</p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-16">
            {(studentServices.length ? studentServices : staticStudent).map(({ id, name, subtitle, icon, bg }) => (
              <Link key={id} to={(isAuthenticated && user?.role === 'customer') ? '/dashboard' : `/service-details/${id}`} className="block group w-full">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`bg-gradient-to-b ${bg} rounded-xl p-4 shadow-lg text-white text-sm overflow-hidden w-full h-full flex flex-col items-center justify-center text-center cursor-pointer transition group-hover:shadow-xl group-hover:-translate-y-0.5`}
                >
                  <div className="p-2 rounded-lg bg-white/20 text-white mb-2">
                    <SafeIcon icon={icon} className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-semibold">{name}</h3>
                  <p className="text-xs mt-1 opacity-90">{subtitle || ''}</p>
                </motion.div>
              </Link>
            ))}
          </div>

          {/* Apply For Job Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">Apply For Job Services</h2>
          </motion.div>

          {/* Banner after Apply For Job Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            {/* Mobile slider */}
            <div className="md:hidden">
              <div ref={mobileBannerRef} className="flex overflow-x-auto no-scrollbar rounded-xl shadow-lg border border-gray-200">
                {[...bannerImages, ...bannerImages].map((src, i) => (
                  <div key={i} className="min-w-full">
                    <img src={src} alt={`How2Apply Banner ${i + 1}`} className="w-full h-auto object-contain" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
            {/* Desktop static banner */}
            <div className="hidden md:block">
              <div className="w-full overflow-hidden rounded-xl shadow-lg border border-gray-200">
                <img
                  src={ban1}
                  alt="How2Apply Banner"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>

          {/* Job & Career Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 mt-16"
          >
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">Job & Career Services</h2>
            <p className="mt-2 text-gray-600">Professional assistance for your career growth and job applications</p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-16">
            {(jobServices.length ? jobServices : staticJobs).map(({ id, name, icon, to, bg }) => (
              <Link key={id} to={(isAuthenticated && user?.role === 'customer') ? '/dashboard' : (to || `/service-details/${id}`)} className="block group w-full">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`bg-gradient-to-br ${bg} rounded-xl p-5 shadow-lg text-white text-sm overflow-hidden w-full h-full flex flex-col items-center justify-center text-center cursor-pointer transition group-hover:shadow-xl group-hover:-translate-y-0.5`}
                >
                  <div className="p-2 rounded-lg bg-white/20 text-white mb-2">
                    <SafeIcon icon={icon} className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-semibold">{name}</h3>
                </motion.div>
              </Link>
            ))}
          </div>

          {/* Other Essential Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">Other Essential Services</h2>
            <p className="mt-2 text-gray-600">Comprehensive solutions for your documentation and verification needs</p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-16">
            {(otherServices.length ? otherServices : staticOther).map(({ id, name, icon, to, bg }) => (
              <Link key={id} to={(isAuthenticated && user?.role === 'customer') ? '/dashboard' : (to || `/service-details/${id}`)} className="block group w-full">
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25 }}
                  className={`bg-gradient-to-br ${bg} rounded-xl p-5 shadow-lg text-white text-sm overflow-hidden w-full h-full flex flex-col items-center justify-center text-center cursor-pointer transition group-hover:shadow-xl group-hover:-translate-y-0.5`}
                >
                  <div className="p-2 rounded-lg bg-white/20 text-white mb-2">
                    <SafeIcon icon={icon} className="w-5 h-5" />
                  </div>
                  <h3 className="text-sm font-semibold">{name}</h3>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-5 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">Why Choose How2Apply</h2>
            <p className="mt-3 text-gray-600 text-sm lg:text-base">Fast, accurate, and hassle-free application support from trusted experts.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-xl p-5 shadow border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-fuchsia-100 text-fuchsia-700">
                  <SafeIcon icon={FiIcons.FiClock} className="w-auto h-auto" />
                </div>
                <h3 className="font-semibold text-gray-900">Save Time</h3>
              </div>
              <p className="mt-3 text-sm text-gray-600">Skip queues and paperwork. We complete forms quickly so you can focus on what matters.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="bg-white rounded-xl p-5 shadow border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                  <SafeIcon icon={FiIcons.FiShield} className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-900">Trusted Accuracy</h3>
              </div>
              <p className="mt-3 text-sm text-gray-600">Experienced operators ensure your applications are error-free and compliant.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-xl p-5 shadow border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
                  <SafeIcon icon={FiIcons.FiSmartphone} className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-900">Do It From Home</h3>
              </div>
              <p className="mt-3 text-sm text-gray-600">End-to-end assistance over phone or chat—no travel required.</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="bg-white rounded-xl p-5 shadow border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
                  <SafeIcon icon={FiIcons.FiHeadphones} className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-900">Extended Support</h3>
              </div>
              <p className="mt-3 text-sm text-gray-600">Support available from 8:00 AM - 11:00 PM for all queries.</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;